import { safeStorage } from '@ps/tools/helpers/safeStorage'
import z from 'zod'

import { AuthStoreType } from '../hooks/useAuthStore'

const anonymousIdSchema = z.string().uuid()

export class IdentityService {
  private static instance: IdentityService | null = null

  private static STORAGE_KEY = '_uaid'
  private isAnonymousId: boolean = true
  private userId: string | null = null

  constructor() {}

  static getInstance(): IdentityService {
    if (!IdentityService.instance) {
      IdentityService.instance = new IdentityService()
    }

    return IdentityService.instance
  }

  private getUserId(user?: AuthStoreType | null) {
    if (this.userId) {
      return { id: this.userId, isAnonymous: this.isAnonymousId }
    }

    // Prioritize authenticated user ID over a stable anonymous ID
    if (user?.id) {
      this.userId = user.id
      this.isAnonymousId = false
      return { id: user.id, isAnonymous: false }
    }

    /**
     * Check LocalStorage for anonymous ID before generating a new one
     * This will ensure we have a stable ID between our analytics platforms
     */
    const storedId = safeStorage.get(IdentityService.STORAGE_KEY, anonymousIdSchema)

    if (storedId) {
      this.userId = storedId
      this.isAnonymousId = true
      return { id: storedId, isAnonymous: true }
    }

    const newId = crypto.randomUUID()
    safeStorage.set(IdentityService.STORAGE_KEY, newId, anonymousIdSchema)
    this.userId = newId
    this.isAnonymousId = true

    return { id: newId, isAnonymous: true }
  }

  public refreshIdentity(user?: AuthStoreType | null) {
    // Clear cached ID to force re-evaluation on next getUserContext call
    this.userId = null
    return this.getUserId(user)
  }

  /**
   * @returns User context for analytics purposes
   */
  public getUserContext(user?: AuthStoreType | null) {
    const { id, isAnonymous } = this.getUserId(user)

    return { kind: 'user', key: id, anonymous: isAnonymous, email: user?.email }
  }
}
